import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Empty, Input, List, Modal, Segmented, Select, Space, Spin, Tag, Typography } from 'antd';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { sendNetworkMessage, SkipList } from './networkMessages';

import css from './TellNetworkModal.module.css';

const { Text } = Typography;
const NOTE_MAX = 500;

// What a job or an RFP can be sent as, in the order offered.
const EVENTS = {
  job_listing: ['jobToTalent', 'jobToSuppliers', 'jobChanged'],
  rfp_listing: ['rfpToSuppliers', 'rfpAddendum'],
};
// An addendum goes to every supplier told about the RFP: nobody can be left out, and it needs its text.
const EVERYONE = ['rfpAddendum'];
// A supplier's alert about a customer's posting, to its consultants or its sub-suppliers, leaves the
// customer's name out unless it chooses to show it.
const FORWARDED = ['jobToConsultants', 'jobToSubSuppliers', 'rfpToSubSuppliers'];
const BAND_COLOR = { strong: 'green', good: 'blue', weak: 'default' };

/**
 * Telling the company's preferred network about one of its live postings, from the posting's menu:
 * the people it fits, grouped Strong / Good / Weak with the reason beside each name, ticked by
 * default when Strong or Good, any of whom the sender can untick; a note; and, once sent, who it
 * reached and who was skipped and why. People held back by a limit or their own setting are shown
 * with when they can next be reached.
 */
const TellNetworkModal = ({ open, posting, initialEvent, events: offered, onClose }) => {
  const intl = useIntl();
  const events = offered || EVENTS[posting?.listingType] || EVENTS.job_listing;
  const [event, setEvent] = useState(initialEvent && events.includes(initialEvent) ? initialEvent : events[0]);
  const [audience, setAudience] = useState('match');
  const [preview, setPreview] = useState({ status: 'idle', items: [], counts: null, error: null });
  const [chosen, setChosen] = useState(new Set());
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [showCustomer, setShowCustomer] = useState(false);
  const everyone = EVERYONE.includes(event);
  const forwarded = FORWARDED.includes(event);

  const load = useCallback(() => {
    if (!open || !posting) return;
    setPreview({ status: 'loading', items: [], counts: null, error: null });
    setResult(null);
    sendNetworkMessage({ action: 'preview', postingId: posting.id, event, audience })
      .then(answer => {
        const items = answer?.items || [];
        setPreview({ status: 'loaded', items, counts: answer?.counts || null, error: null });
        setChosen(new Set(items.filter(i => i.included).map(i => i.entryId)));
      })
      .catch(error => setPreview({ status: 'failed', items: [], counts: null, error: error?.error || null }));
  }, [open, posting, event, audience]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = entryId =>
    setChosen(current => {
      const next = new Set(current);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });

  const send = () => {
    setSending(true);
    sendNetworkMessage({
      action: 'send',
      postingId: posting.id,
      event,
      recipients: everyone ? [] : [...chosen],
      ...(note.trim() ? { note: note.trim() } : {}),
      ...(forwarded ? { showCustomer } : {}),
    })
      .then(answer => setResult(answer))
      .catch(error => setResult({ error: error?.error || 'failed' }))
      .finally(() => setSending(false));
  };

  const count = everyone ? preview.items.filter(i => !i.blocked).length : chosen.size;
  const canSend = preview.status === 'loaded' && count > 0 && (!everyone || !!note.trim()) && !result;

  const content = () => {
    if (preview.status === 'loading' || preview.status === 'idle') {
      return (
        <div className={css.centre} data-testid="tell-network-loading">
          <Spin />
        </div>
      );
    }
    if (preview.status === 'failed') {
      return preview.error === 'PLAN_REQUIRED' ? (
        <Alert type="info" showIcon message={intl.formatMessage({ id: 'TellNetworkModal.planRequired' })} />
      ) : (
        <Alert
          type="error"
          showIcon
          message={intl.formatMessage({ id: 'TellNetworkModal.loadError' })}
          action={
            <Button size="small" onClick={load}>
              <FormattedMessage id="TellNetworkModal.retry" />
            </Button>
          }
        />
      );
    }
    if (!preview.items.length) return <Empty description={intl.formatMessage({ id: 'TellNetworkModal.empty' })} />;
    return (
      <List
        className={css.list}
        dataSource={preview.items}
        renderItem={item => (
          <List.Item key={item.entryId} data-testid={`tell-network-item-${item.entryId}`}>
            <Space align="start">
              <Checkbox
                checked={everyone ? !item.blocked : chosen.has(item.entryId)}
                disabled={everyone || !!item.blocked}
                onChange={() => toggle(item.entryId)}
              />
              <div>
                <Text strong>{item.name}</Text>{' '}
                <Tag color={BAND_COLOR[item.band] || 'default'}>
                  <FormattedMessage id={`TellNetworkModal.band.${item.band || 'none'}`} />
                </Tag>
                {item.reasons?.length ? <div className={css.reasons}>{item.reasons.join(' · ')}</div> : null}
                {item.blocked ? (
                  <div className={css.blocked}>
                    <FormattedMessage
                      id={`TellNetworkModal.blocked.${item.blocked.cap}`}
                      values={{ date: item.blocked.nextAt ? intl.formatDate(item.blocked.nextAt, { month: 'short', day: 'numeric' }) : null }}
                    />
                  </div>
                ) : null}
              </div>
            </Space>
          </List.Item>
        )}
      />
    );
  };

  return (
    <Modal
      open={open}
      title={intl.formatMessage({ id: 'TellNetworkModal.title' }, { title: posting?.title || '' })}
      onCancel={onClose}
      destroyOnClose
      footer={
        result ? (
          <Button type="primary" onClick={onClose}>
            <FormattedMessage id="TellNetworkModal.done" />
          </Button>
        ) : (
          <Space>
            <Button onClick={onClose}>
              <FormattedMessage id="TellNetworkModal.cancel" />
            </Button>
            <Button type="primary" disabled={!canSend} loading={sending} onClick={send}>
              <FormattedMessage id="TellNetworkModal.send" values={{ count }} />
            </Button>
          </Space>
        )
      }
    >
      <Space direction="vertical" className={css.root}>
        <Select
          value={event}
          onChange={value => setEvent(value)}
          options={events.map(e => ({ value: e, label: intl.formatMessage({ id: `TellNetworkModal.event.${e}` }) }))}
          disabled={!!result}
        />
        {everyone ? null : (
          <Segmented
            value={audience}
            onChange={value => setAudience(value)}
            options={[
              { value: 'match', label: intl.formatMessage({ id: 'TellNetworkModal.audience.match' }) },
              { value: 'all', label: intl.formatMessage({ id: 'TellNetworkModal.audience.all' }) },
            ]}
            disabled={!!result}
          />
        )}
        {preview.counts ? (
          <Text type="secondary">
            <FormattedMessage
              id="TellNetworkModal.counts"
              values={{ match: preview.counts.match, audience: preview.counts.audience, rate: Math.round((preview.counts.notRelevantRate || 0) * 100) }}
            />
          </Text>
        ) : null}
        {result ? null : content()}
        {result || !forwarded ? null : (
          <Checkbox checked={showCustomer} onChange={e => setShowCustomer(e.target.checked)}>
            <FormattedMessage id="TellNetworkModal.showCustomer" />
          </Checkbox>
        )}
        {result ? null : (
          <label className={css.note}>
            <span>{intl.formatMessage({ id: everyone ? 'TellNetworkModal.addendum' : 'TellNetworkModal.note' })}</span>
            <Input.TextArea value={note} onChange={e => setNote(e.target.value)} maxLength={NOTE_MAX} rows={3} />
          </label>
        )}
        {result && !result.error ? (
          <>
            <Alert type="success" showIcon message={intl.formatMessage({ id: 'TellNetworkModal.result' }, { sent: result.sent, skipped: result.skipped?.length || 0 })} />
            <SkipList skipped={result.skipped} />
          </>
        ) : null}
        {result && result.error ? (
          <Alert
            type={result.error === 'PLAN_REQUIRED' ? 'info' : 'error'}
            showIcon
            message={intl.formatMessage({ id: result.error === 'PLAN_REQUIRED' ? 'TellNetworkModal.planRequired' : 'TellNetworkModal.sendError' })}
          />
        ) : null}
      </Space>
    </Modal>
  );
};

export default TellNetworkModal;
